import moment from 'moment';

import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import DocumentStore from 'notebook/documents/DocumentStore';
import {isInStore, hasUnsavedChanges} from 'notebook/entries/BaseEntryUtil';
import * as DocumentEditorLockReduxActions from 'notebook/realtime/DocumentEditorLockReduxActions';
import {DocumentEditorLock} from 'notebook/realtime/reduceDocumentEditorLockState';
import {isCurrentClient} from 'realtime/util/RealtimeUtil';
import {BenchlingState} from 'redux-stores/reduceApp';
import {getStore} from 'redux-stores/Store';
import APIUtil from 'util/APIUtil';

/**
 * Client-side coordination for document editor locks. The authoritative lock lives server-side in
 * document_editor_lock (see benchling/notebook/realtime/).
 *
 * Server lock is acquired on save or via forceAcquireLocks and expires after
 * ELN_REALTIME_DOCUMENT_EDITOR_LOCK_DURATION_SECONDS
 *
 * This module waits ELN_RELEASE_DOCUMENT_LOCK_INTERVAL ms after save or unmount, then asks the
 * server to release the lock.
 *
 * RELEASE_LOCK_TOMBSTONE is a client-only flag that blocks releaseLocks()
 * Does not expire when the server lock TTL elapses. Clear it with clearReleaseLockTimeout or acquireLocks
 */

export const RELEASE_LOCK_TOMBSTONE = -1;
export const releaseLockTimeoutMap: Record<string, NodeJS.Timeout | typeof RELEASE_LOCK_TOMBSTONE> = {};

const DocumentEditorLockUtil = {
  /**
   * Schedules the release of locks for a given set of documents after a delay
   *
   * Early returns and no-ops while a document has a release tombstone (see `forceAcquireLocks`)
   * or unsaved changes.
   */
  releaseLocks(documentIds: Array<string>): void {
    if (BenchlingConfig.ELN_RELEASE_DOCUMENT_LOCK_INTERVAL <= -1) {
      return;
    }

    if (
      documentIds.some(
        (documentId) =>
          releaseLockTimeoutMap[documentId] === RELEASE_LOCK_TOMBSTONE ||
          (isInStore(documentId) && hasUnsavedChanges(documentId))
      )
    ) {
      return;
    }

    for (const documentId of documentIds) {
      DocumentEditorLockUtil.clearReleaseLockTimeout(documentId);
      releaseLockTimeoutMap[documentId] = RELEASE_LOCK_TOMBSTONE;
    }

    const timeout = setTimeout(() => {
      for (const documentId of documentIds) {
        // If any related document has unsaved changes don't release the locks
        if (isInStore(documentId) && hasUnsavedChanges(documentId)) {
          return;
        }
      }
      if (documentIds.length > 0) {
        getStore().dispatch(DocumentEditorLockReduxActions.releaseLocks(documentIds));
      }
    }, BenchlingConfig.ELN_RELEASE_DOCUMENT_LOCK_INTERVAL);

    for (const documentId of documentIds) {
      releaseLockTimeoutMap[documentId] = timeout;
    }
  },

  /**
   * Cancels any pending lock releases (scheduled by releaseLocks) as well as any release tombstones.
   * Does not release the server lock or change Redux lock state
   */
  clearReleaseLockTimeout(documentId: string): void {
    if (releaseLockTimeoutMap[documentId]) {
      // If the value exists and it is not a tombstone value, its a timeout so clear it
      if (releaseLockTimeoutMap[documentId] !== RELEASE_LOCK_TOMBSTONE) {
        clearTimeout(releaseLockTimeoutMap[documentId]);
      }
      delete releaseLockTimeoutMap[documentId];
    }
  },

  /**
   * Immediately acquires the server lock (network request) and sets a release tombstone so releaseLocks
   * cannot drop the lock. Returns every document it locked, which includes related documents.
   */
  forceAcquireLocks(documentId: string): Array<string> {
    const documentsToLock = getDocumentsToLock(documentId);
    for (const documentId of documentsToLock) {
      DocumentEditorLockUtil.clearReleaseLockTimeout(documentId);
      releaseLockTimeoutMap[documentId] = RELEASE_LOCK_TOMBSTONE;
    }

    if (documentsToLock.length > 0) {
      getStore().dispatch(
        DocumentEditorLockReduxActions.acquireLocks(documentsToLock, true /* forceNetwork */)
      );
    }
    return documentsToLock;
  },

  /**
   * Optimistically updates Redux lock state and clears any release tombstone. Does not hit the network;
   * the server lock is acquired/refreshed on the next document save
   *
   * Invoked on every edit via `DocumentActions.update`.
   */
  acquireLocks(documentId: string): void {
    const documentsToLock = getDocumentsToLock(documentId);
    for (const documentId of documentsToLock) {
      DocumentEditorLockUtil.clearReleaseLockTimeout(documentId);
    }

    if (documentsToLock.length > 0) {
      getStore().dispatch(DocumentEditorLockReduxActions.acquireLocks(documentsToLock));
    }
  },

  /** Returns the local Redux lock for a document, if the document is in the entry store. */
  getLock(documentId: string): DocumentEditorLock | undefined {
    return isInStore(documentId) ? getStore().getState().ui.documentEditorLockState[documentId] : undefined;
  },

  /**
   * Checks Redux state for whether another client's lock should block editing in the UI.
   */
  isLockedInRealtime(documentEditorLock?: DocumentEditorLock): boolean {
    if (documentEditorLock && !isCurrentClient(documentEditorLock.clientId)) {
      if (moment().isBefore(moment(documentEditorLock.expiresAt))) {
        return true;
      }
    }
    return false;
  },

  mapDocumentEditorLockStateToProps(
    state: BenchlingState,
    fileId: string
  ): {documentEditorLock?: DocumentEditorLock} {
    const documentEditorLock = state.ui.documentEditorLockState[fileId];
    if (documentEditorLock) {
      return {
        documentEditorLock,
      };
    }
    return {};
  },
};

/**
 * Returns all api identifiers of objects that should be locked/unlocked alongside the given document
 * For example, worksheet step documents should be locked and unlocked together.
 */
function getDocumentsToLock(documentId: string): Array<string> {
  const documentsToLock: Array<string> = [documentId];
  if (
    APIUtil.apiIdToType(documentId) === ModelTypeConstants.DOCUMENT &&
    BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS
  ) {
    const relatedDocumentIds = DocumentStore.getRelatedDocumentIds(documentId);
    documentsToLock.push(...relatedDocumentIds.filter((id) => id !== documentId));
  }
  return documentsToLock;
}

export default DocumentEditorLockUtil;
